import axios, { AxiosHeaders } from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { Router } from 'vue-router';

import type { useAuthStore } from '@app/stores/auth';
import { useAttendanceLockStore } from '@features/attendance/attendanceLockStore';
import {
  ATTENDANCE_REQUIRED_CODE,
  ATTENDANCE_STATUS_UNAVAILABLE_CODE
} from '@features/attendance/attendanceErrors';
import { resolveApiBase } from '@shared/api/apiBase';
import { calendarHttp } from '@shared/api/calendarClient';
import { kanbanHttp } from '@shared/api/kanbanClient';
import { mesHttp } from '@shared/api/mesClient';
import { processEngineHttp } from '@shared/api/processEngineClient';
import { usersHttp } from '@shared/api/usersClient';

const redirectToLogin = async (router: Router) => {
  const currentRoute = router.currentRoute.value;
  const isLogin = currentRoute.name === 'login' || currentRoute.name === 'login-qr';
  if (isLogin) return;

  const query = currentRoute.meta?.public ? undefined : { redirect: currentRoute.fullPath };
  await router.replace({ name: 'login', query }).catch(() => {
    // ignore navigation duplicate errors
  });
};

export const setupAxiosInterceptors = (
  authStore: ReturnType<typeof useAuthStore>,
  router: Router
) => {
  const attendanceLock = useAttendanceLockStore();
  const refreshClient = axios.create();
  type SessionSnapshot = ReturnType<typeof authStore.captureSession>;
  type AuthRequest = InternalAxiosRequestConfig & { _retry?: boolean; _authSession?: SessionSnapshot };
  let refreshPromise: Promise<string | null> | null = null;
  let refreshSession: SessionSnapshot = null;
  let redirectPromise: Promise<void> | null = null;
  const API_BASE = resolveApiBase(import.meta.env.VITE_AUTH_API_URL);

  const buildUrl = (path: string) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    if (!API_BASE) {
      return normalizedPath;
    }
    if (normalizedPath.startsWith(API_BASE)) {
      return normalizedPath;
    }
    return `${API_BASE}${normalizedPath}`;
  };

  const refreshToken = async (): Promise<string | null> => {
    if (!authStore.refreshToken) return null;
    if (refreshPromise && authStore.isSessionCurrent(refreshSession)) return refreshPromise;
    const captured = authStore.captureSession();
    if (!captured) return null;
    refreshSession = captured;
    const currentPromise = refreshClient
      .post(buildUrl('/auth/refresh'), { refreshToken: authStore.refreshToken })
      .then((response) => {
        if (!authStore.isSessionCurrent(captured)) { authStore.syncSession(); return null; }
        const payload = response.data;
        if (!payload?.accessToken) {
          return null;
        }
        authStore.persistSession(payload);
        return payload.accessToken as string;
      })
      .catch(() => null)
      .finally(() => {
        if (refreshPromise === currentPromise) { refreshPromise = null; refreshSession = null; }
      });
    refreshPromise = currentPromise;
    return currentPromise;
  };

  const expireSessionAndRedirect = async () => {
    if (redirectPromise) {
      return redirectPromise;
    }

    authStore.clearSession();
    redirectPromise = redirectToLogin(router).finally(() => {
      redirectPromise = null;
    });

    return redirectPromise;
  };

  const registerAuthInterceptors = (client: AxiosInstance) => {
    client.interceptors.request.use((config: AuthRequest) => {
      config._authSession = authStore.captureSession();
      if (!config._authSession) throw new Error('Сотрудник изменился. Повторите действие после входа.');
      if (authStore.token) {
        const headers =
          config.headers instanceof AxiosHeaders ? config.headers : new AxiosHeaders(config.headers);
        headers.set('Authorization', `Bearer ${authStore.token}`);
        config.headers = headers;
      }
      return config;
    });

    client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const captured = authStore.captureSession();
        const status = error?.response?.status;
        const responseData = error.response?.data as { code?: unknown } | undefined;
        const isAttendanceError =
          responseData?.code === ATTENDANCE_REQUIRED_CODE ||
          responseData?.code === ATTENDANCE_STATUS_UNAVAILABLE_CODE;
        const originalRequest = error.config as AuthRequest | undefined;
        if (originalRequest && !authStore.isSessionCurrent(originalRequest._authSession ?? null)) {
          authStore.syncSession(); return Promise.reject(error);
        }
        if (isAttendanceError) {
          void attendanceLock.refresh({ force: true });
          const currentRoute = router.currentRoute.value;
          const canStay =
            currentRoute.meta?.public ||
            currentRoute.name === 'dashboard' ||
            currentRoute.name === 'attendance-scan';
          if (!canStay) {
            await router.replace({ name: 'dashboard' }).catch(() => undefined);
          }
          return Promise.reject(error);
        }

        if (!originalRequest) return Promise.reject(error);
        const isAuthEndpoint =
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/refresh');

        if ((status === 401 || status === 403) && !isAuthEndpoint && !originalRequest._retry) {
          originalRequest._retry = true;
          const newToken = await refreshToken();
          const refreshedCurrentSession = Boolean(newToken && authStore.token === newToken &&
            authStore.isSessionCurrent(authStore.captureSession()));
          if (!authStore.isSessionCurrent(captured) && !refreshedCurrentSession) return Promise.reject(error);
          if (newToken) {
            const headers =
              originalRequest.headers instanceof AxiosHeaders
                ? originalRequest.headers
                : new AxiosHeaders(originalRequest.headers);
            headers.set('Authorization', `Bearer ${newToken}`);
            originalRequest.headers = headers;
            return client.request(originalRequest);
          }

          await expireSessionAndRedirect();
        } else if (status === 401 && !isAuthEndpoint && originalRequest._retry) {
          await expireSessionAndRedirect();
        }

        return Promise.reject(error);
      }
    );
  };

  [axios, mesHttp, usersHttp, processEngineHttp, kanbanHttp, calendarHttp].forEach(registerAuthInterceptors);
};
